import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getAdminInventory,
  adjustInventoryStock,
  getInventoryMovements,
  getInventoryConsumption,
  migrateMenuRecipes,
  seedBeverageInventory,
  formatApiDetail,
} from '../lib/api';
import {
  ingredientUsesUnits,
  ingredientUsesUnitsFromDisplay,
  ingredientStockQuantity,
  ingredientLowStockQuantity,
  ingredientStockUnitLabel,
  formatQuantityInput,
  formatConsumptionQuantity,
  consumptionUnitLabelFromItem,
  inventoryMovementReasonLabel,
  sumProductionConsumption,
} from '../lib/inventoryUnits';
import {
  todayMexicoYmd,
  formatMexicoDateLong,
  formatTimeMexico,
  msToMexicoYmd,
} from '../lib/dateMexico';
import OrderWeekDayPicker from '../components/OrderWeekDayPicker';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Warehouse, Package, CupSoda, BarChart3 } from 'lucide-react';

function formatMovementDateTime(iso) {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  return `${formatMexicoDateLong(msToMexicoYmd(ms))} · ${formatTimeMexico(iso)}`;
}

function ConsumptionTable({ items }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Insumo</TableHead>
            <TableHead>Consumido</TableHead>
            <TableHead className="text-right">Movimientos</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.ingredient_id}>
              <TableCell className="font-medium">{item.ingredient_name}</TableCell>
              <TableCell className="tabular-nums">
                {formatConsumptionQuantity(item)} {consumptionUnitLabelFromItem(item)}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {item.movement_count ?? 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function sortByName(items) {
  return [...items].sort((a, b) =>
    String(a.name || a.ingredient_name || '').localeCompare(
      String(b.name || b.ingredient_name || ''),
      'es',
    ),
  );
}

function InventoryTable({ items, onHistory, onAdjust }) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="overflow-x-auto -mx-1 px-1">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Insumo</TableHead>
          <TableHead>Stock</TableHead>
          <TableHead>Mínimo</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((ing) => {
          const unitLabel = ingredientStockUnitLabel(ing);
          const stockQty = ingredientStockQuantity(ing);
          const lowQty = ingredientLowStockQuantity(ing);
          const stockText =
            unitLabel === 'pzas' ? String(Math.round(stockQty)) : stockQty.toFixed(3);
          const lowText =
            lowQty > 0
              ? unitLabel === 'pzas'
                ? String(Math.round(lowQty))
                : lowQty.toFixed(3)
              : '—';
          return (
            <TableRow key={ing.id}>
              <TableCell className="font-medium">{ing.name}</TableCell>
              <TableCell className="tabular-nums">
                {stockText} {unitLabel}
              </TableCell>
              <TableCell className="tabular-nums text-muted-foreground">
                {lowText === '—' ? '—' : `${lowText} ${unitLabel}`}
              </TableCell>
              <TableCell>
                {ing.is_low_stock ? (
                  <Badge variant="destructive">Bajo</Badge>
                ) : (
                  <Badge variant="secondary">OK</Badge>
                )}
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button size="sm" variant="outline" onClick={() => onHistory(ing)}>
                  Historial
                </Button>
                <Button size="sm" onClick={() => onAdjust(ing)}>
                  Ajustar
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
  );
}

export default function AdminInventory() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const superAdmin = Boolean(user && isSuperAdmin(user.role));
  const [rows, setRows] = useState([]);
  const [itemsNoRecipe, setItemsNoRecipe] = useState(0);
  const [lowOnly, setLowOnly] = useState(() => searchParams.get('low') === '1');
  const [loading, setLoading] = useState(true);
  const [adjustIng, setAdjustIng] = useState(null);
  const [deltaKg, setDeltaKg] = useState('');
  const [lowStockKg, setLowStockKg] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyIng, setHistoryIng] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDate, setHistoryDate] = useState(() => todayMexicoYmd());
  const [historyMode, setHistoryMode] = useState('day');
  const [consumptionDate, setConsumptionDate] = useState(() => todayMexicoYmd());
  const [consumptionItems, setConsumptionItems] = useState([]);
  const [consumptionLoading, setConsumptionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminInventory(lowOnly);
      setRows(data.ingredients || []);
      setItemsNoRecipe(data.items_without_recipe_count || 0);
    } catch {
      toast.error('No se pudo cargar el almacén');
    } finally {
      setLoading(false);
    }
  }, [lowOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const loadConsumption = useCallback(async () => {
    setConsumptionLoading(true);
    try {
      const data = await getInventoryConsumption(consumptionDate);
      setConsumptionItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      toast.error('No se pudo cargar el consumo del día');
      setConsumptionItems([]);
    } finally {
      setConsumptionLoading(false);
    }
  }, [consumptionDate]);

  useEffect(() => {
    loadConsumption();
  }, [loadConsumption]);

  const loadHistory = useCallback(async () => {
    if (!historyIng?.id) return;
    setHistoryLoading(true);
    try {
      const opts =
        historyMode === 'week'
          ? { week: historyDate, limit: 1000 }
          : { date: historyDate, limit: 500 };
      const list = await getInventoryMovements(historyIng.id, opts);
      setMovements(Array.isArray(list) ? list : []);
    } catch {
      toast.error('No se pudo cargar el historial');
      setMovements([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyIng, historyDate, historyMode]);

  useEffect(() => {
    if (historyIng) {
      loadHistory();
    }
  }, [historyIng, loadHistory]);

  const openHistory = (ing) => {
    setHistoryIng(ing);
    setHistoryDate(todayMexicoYmd());
    setHistoryMode('day');
    setMovements([]);
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    if (!adjustIngLive) return;
    const usesUnits = ingredientUsesUnits(adjustIngLive);
    const prevMin = ingredientLowStockQuantity(adjustIngLive);
    const minParsed = lowStockKg === '' ? null : parseFloat(lowStockKg);
    const minChanged =
      minParsed != null && Number.isFinite(minParsed) && minParsed !== prevMin;
    const deltaParsed = deltaKg === '' ? 0 : parseFloat(deltaKg);
    const hasDelta = Number.isFinite(deltaParsed) && deltaParsed !== 0;
    if (!hasDelta && !minChanged) {
      toast.error('Indica un cambio de stock o modifica el mínimo');
      return;
    }
    if (minParsed != null && (!Number.isFinite(minParsed) || minParsed < 0)) {
      toast.error('El mínimo debe ser un número ≥ 0');
      return;
    }
    if (usesUnits && hasDelta && !Number.isInteger(deltaParsed)) {
      toast.error('El cambio de stock en piezas debe ser un número entero');
      return;
    }
    setSaving(true);
    try {
      await adjustInventoryStock(adjustIngLive.id, {
        ...(usesUnits
          ? {
              deltaUnits: hasDelta ? deltaParsed : undefined,
              lowStockUnits: minChanged ? minParsed : undefined,
            }
          : {
              deltaKg: hasDelta ? deltaParsed : undefined,
              lowStockKg: minChanged ? minParsed : undefined,
            }),
        note: adjustNote.trim(),
      });
      toast.success('Guardado');
      setAdjustIng(null);
      setDeltaKg('');
      setLowStockKg('');
      setAdjustNote('');
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'No se pudo ajustar'));
    } finally {
      setSaving(false);
    }
  };

  const runMigrate = async () => {
    if (!window.confirm('¿Copiar ingredient_ids a recetas con 0 g para completar en Menú?')) return;
    try {
      const r = await migrateMenuRecipes();
      toast.success(`Actualizados ${r.items_updated || 0} productos`);
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'Error'));
    }
  };

  const beverageRows = useMemo(
    () => sortByName(rows.filter((ing) => ingredientUsesUnits(ing))),
    [rows],
  );
  const kitchenRows = useMemo(
    () => sortByName(rows.filter((ing) => !ingredientUsesUnits(ing))),
    [rows],
  );

  const beverageConsumption = useMemo(
    () =>
      sortByName(
        consumptionItems.filter((item) => ingredientUsesUnitsFromDisplay(item.unit_display)),
      ),
    [consumptionItems],
  );
  const kitchenConsumption = useMemo(
    () =>
      sortByName(
        consumptionItems.filter((item) => !ingredientUsesUnitsFromDisplay(item.unit_display)),
      ),
    [consumptionItems],
  );

  const historyProductionTotal = useMemo(
    () => sumProductionConsumption(movements, historyIng || {}),
    [movements, historyIng],
  );
  const historyUnitLabel = ingredientStockUnitLabel(historyIng || {});

  const adjustIngLive = useMemo(() => {
    if (!adjustIng?.id) return null;
    return rows.find((r) => r.id === adjustIng.id) || adjustIng;
  }, [adjustIng, rows]);

  const openAdjust = (ing) => {
    const fresh = rows.find((r) => r.id === ing.id) || ing;
    setAdjustIng(fresh);
    setDeltaKg('');
    setLowStockKg(formatQuantityInput(fresh, ingredientLowStockQuantity(fresh)));
    setAdjustNote('');
  };

  const runSeedBeverages = async () => {
    if (
      !window.confirm(
        '¿Crear insumos en piezas y vincular recetas para bebidas/complementos sin receta?',
      )
    ) {
      return;
    }
    try {
      const r = await seedBeverageInventory();
      const created = r.ingredients_created || 0;
      const linked = r.items_linked || 0;
      toast.success(`Insumos creados: ${created}. Productos vinculados: ${linked}.`);
      load();
    } catch (err) {
      toast.error(formatApiDetail(err.response?.data?.detail, 'Error al sincronizar bebidas'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="flex flex-wrap items-start gap-3">
        <Warehouse className="w-8 h-8 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-muted-foreground">
            Stock de insumos en kg o piezas. Cocina descuenta al marcar atendido; caja descuenta bebidas y
            complementos al cerrar venta.
          </p>
          {itemsNoRecipe > 0 ? (
            <p className="text-sm text-amber-700 mt-2">
              {itemsNoRecipe} producto(s) sin receta —{' '}
              <Link to="/admin/menu" className="underline font-medium">
                configurar en Menú
              </Link>
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={lowOnly} onCheckedChange={setLowOnly} id="low-stock" />
          <Label htmlFor="low-stock" className="text-sm cursor-pointer">
            Solo stock bajo
          </Label>
        </div>
        <Button variant="outline" size="sm" onClick={runSeedBeverages}>
          Sincronizar bebidas
        </Button>
        {superAdmin ? (
          <Button variant="outline" size="sm" onClick={runMigrate}>
            Migrar recetas
          </Button>
        ) : null}
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/menu" className="gap-1">
            <Package className="w-4 h-4" />
            Menú
          </Link>
        </Button>
      </div>

      <Card data-testid="inventory-consumption-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-primary" />
            Consumo del día
          </CardTitle>
          <p className="text-sm text-muted-foreground font-normal">
            Solo producción (cocina y caja). No incluye ajustes manuales.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <OrderWeekDayPicker
            selectedYmd={consumptionDate}
            onSelectYmd={setConsumptionDate}
            data-testid="inventory-consumption-picker"
          />
          {consumptionLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Cargando consumo…</p>
          ) : consumptionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Sin consumo de producción este día
            </p>
          ) : (
            <div className="space-y-6">
              {beverageConsumption.length > 0 ? (
                <div>
                  <p className="text-sm font-medium mb-2">Bebidas y complementos</p>
                  <ConsumptionTable items={beverageConsumption} />
                </div>
              ) : null}
              {kitchenConsumption.length > 0 ? (
                <div>
                  <p className="text-sm font-medium mb-2">Insumos de cocina</p>
                  <ConsumptionTable items={kitchenConsumption} />
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent>
            <p className="text-muted-foreground py-8 text-center">Cargando…</p>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-muted-foreground py-8 text-center">Sin insumos registrados</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CupSoda className="w-5 h-5 text-primary" />
                Bebidas y complementos
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Stock en piezas. Caja descuenta al cerrar venta.
              </p>
            </CardHeader>
            <CardContent>
              {beverageRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Sin bebidas registradas. Usa «Sincronizar bebidas» o créalas en Menú con unidad pzas.
                </p>
              ) : (
                <InventoryTable
                  items={beverageRows}
                  onHistory={openHistory}
                  onAdjust={openAdjust}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-primary" />
                Insumos de cocina
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Stock en kilogramos. Cocina descuenta al marcar atendido.
              </p>
            </CardHeader>
            <CardContent>
              {kitchenRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Sin insumos de cocina registrados.
                </p>
              ) : (
                <InventoryTable
                  items={kitchenRows}
                  onHistory={openHistory}
                  onAdjust={openAdjust}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog
        open={Boolean(adjustIng)}
        onOpenChange={(o) => {
          if (!o) {
            setAdjustIng(null);
            setDeltaKg('');
            setLowStockKg('');
            setAdjustNote('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar stock — {adjustIngLive?.name}</DialogTitle>
          </DialogHeader>
          <form key={adjustIngLive?.id || 'closed'} onSubmit={submitAdjust} className="space-y-3">
            {(() => {
              const ing = adjustIngLive || {};
              const unitLabel = ingredientStockUnitLabel(ing);
              const stockQty = ingredientStockQuantity(ing);
              const stockText = formatQuantityInput(ing, stockQty);
              const usesUnits = ingredientUsesUnits(ing);
              const lowQty = ingredientLowStockQuantity(ing);
              return (
                <>
            <p className="text-sm text-muted-foreground rounded-lg border bg-muted/30 p-3">
              Stock actual:{' '}
              <strong className="text-foreground">
                {stockText} {unitLabel}
              </strong>
              {lowQty > 0 ? (
                <span className="block mt-1 text-xs">
                  Mínimo configurado: {formatQuantityInput(ing, lowQty)} {unitLabel}
                </span>
              ) : null}
            </p>
            <div>
              <Label>Mínimo ({unitLabel}) — alerta de stock bajo</Label>
              <Input
                type="number"
                min="0"
                step={usesUnits ? '1' : '0.001'}
                value={lowStockKg}
                onChange={(e) => setLowStockKg(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Usa 0 si no quieres alerta. El estado «Bajo» aparece cuando el stock es ≤ este valor.
              </p>
            </div>
            <div>
              <Label>Cambio de stock ({unitLabel})</Label>
              <Input
                type="number"
                step={usesUnits ? '1' : '0.001'}
                value={deltaKg}
                onChange={(e) => setDeltaKg(e.target.value)}
                placeholder="Opcional: + entrada, − salida"
                className="mt-1"
              />
            </div>
                </>
              );
            })()}
            <div>
              <Label>Nota (opcional)</Label>
              <Input
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button type="submit" className="w-full" disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar ajuste'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(historyIng)}
        onOpenChange={(o) => {
          if (!o) {
            setHistoryIng(null);
            setMovements([]);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Movimientos — {historyIng?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="inventory-history-filters">
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={historyMode === 'day' ? 'default' : 'outline'}
                onClick={() => setHistoryMode('day')}
              >
                Día
              </Button>
              <Button
                type="button"
                size="sm"
                variant={historyMode === 'week' ? 'default' : 'outline'}
                onClick={() => setHistoryMode('week')}
              >
                Semana
              </Button>
            </div>
            <OrderWeekDayPicker
              selectedYmd={historyDate}
              onSelectYmd={setHistoryDate}
              data-testid="inventory-history-picker"
            />
            {!historyLoading && movements.length > 0 ? (
              <p className="text-sm text-muted-foreground rounded-lg border bg-muted/30 p-3">
                {movements.length} movimiento{movements.length === 1 ? '' : 's'}
                {historyProductionTotal > 0 ? (
                  <>
                    {' '}
                    · consumo de producción:{' '}
                    <strong className="text-foreground">
                      {ingredientUsesUnits(historyIng || {})
                        ? String(Math.round(historyProductionTotal))
                        : historyProductionTotal.toFixed(3)}{' '}
                      {historyUnitLabel}
                    </strong>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
          {historyLoading ? (
            <p className="text-sm text-muted-foreground">Cargando historial…</p>
          ) : movements.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sin movimientos en el {historyMode === 'week' ? 'periodo seleccionado' : 'día seleccionado'}
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {movements.map((m) => {
                const usesUnits = ingredientUsesUnits(historyIng || {});
                const delta = usesUnits ? m.delta_grams : m.delta_kg;
                const unitLabel = ingredientStockUnitLabel(historyIng || {});
                const deltaText = usesUnits
                  ? String(Math.round(delta))
                  : delta.toFixed(3);
                return (
                  <li key={m.id} className="border rounded-md p-2">
                    <div className="flex justify-between gap-2">
                      <span className={delta < 0 ? 'text-red-700' : 'text-green-700'}>
                        {delta > 0 ? '+' : ''}
                        {deltaText} {unitLabel}
                      </span>
                      <span className="text-muted-foreground text-xs text-right shrink-0">
                        {formatMovementDateTime(m.created_at)}
                      </span>
                    </div>
                    <p className="text-muted-foreground">{inventoryMovementReasonLabel(m.reason)}</p>
                    {m.order_number ? <p>Pedido #{m.order_number}</p> : null}
                    {m.note ? <p className="text-xs italic">{m.note}</p> : null}
                    {m.user_name ? <p className="text-xs">{m.user_name}</p> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
